/**
 * Client-safe activity date rules — pure functions, no server/Firestore deps,
 * safe to import from 'use client' components (e.g. the checkout cross-sell).
 *
 * ⚠️ MUST stay in sync with the booking validator in
 * `functions/src/utils/availability.ts → isActivityDateBlocked()`. That is the
 * Cloud Function that actually rejects a booking with NOT_AVAILABLE
 * ("<activity> is not available on <date> (date is blocked)").
 *
 * The backend validator blocks a date IFF it is in `blockedDates`, and exempts
 * transport (flat-rate always-available transfers). We mirror that contract
 * exactly so the UI never OFFERS a date the backend will reject, and never
 * HIDES a date the backend would accept. We intentionally do NOT filter by
 * `availableDays` here, because the booking validator does not — adding it
 * would make the client stricter than the source of truth.
 */

export interface ActivityDateRules {
  /** ActivityCategory; only 'transport' changes blocking behavior. */
  category?: string;
  /** Admin-blocked dates (YYYY-MM-DD). */
  blockedDates?: string[];
}

/**
 * True when the activity cannot be booked on `date`.
 * Mirror of functions/src/utils/availability.ts:isActivityDateBlocked.
 */
export function isActivityDateBlocked(activity: ActivityDateRules, date: string): boolean {
  if (activity.category === 'transport') return false;
  return activity.blockedDates?.includes(date) ?? false;
}

/**
 * The effective set of blocked dates for an activity — empty for transport.
 * Mirror of functions/src/utils/availability.ts:getEffectiveBlockedDates.
 */
export function getEffectiveBlockedDates(activity: ActivityDateRules): string[] {
  if (activity.category === 'transport') return [];
  return activity.blockedDates ?? [];
}

/**
 * True when the activity has at least one selectable (non-blocked) date within
 * the given range. Used to decide whether to OFFER an activity at all in the
 * checkout cross-sell — there's no point recommending something the guest
 * cannot book on any of their stay nights. Transport is always offerable
 * (never blocked), consistent with isActivityDateBlocked.
 *
 * Note: an empty `dates` array yields false (no dates → nothing selectable);
 * callers without a stay range should decide separately whether to offer.
 */
export function hasSelectableDateInRange(
  activity: ActivityDateRules,
  dates: string[]
): boolean {
  return dates.some((date) => !isActivityDateBlocked(activity, date));
}
